import React, { useState, useEffect, useRef } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Type,
  Palette,
  HeadingIcon,
  FormInput,
  Hash,
  Phone,
  Mail,
  MessageSquare,
  ChevronDownSquare,
  CircleDot,
  CheckSquare,
  FileUp,
  Minus,
  Calendar,
  Copy,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import { FormComponentWithDialog } from "./FormComponent"; // Use the wrapper with confirmation dialog
import { DraggableComponent } from "./DraggableComponent"; // Ensure this is the correct import
import { FieldOptionsDrawer } from "./FieldOptionsDrawer"; // Import the FieldOptionsDrawer

const formComponents = [
  {
    id: "heading",
    type: "heading",
    label: "Heading",
    sublabel: "",
    placeholder: "Enter heading text ",
    icon: HeadingIcon,
  },
  {
    id: "text",
    type: "text",
    label: "Text Input",
    sublabel: "",
    placeholder: "Enter text ",
    max_length: "",
    icon: FormInput,
  },
  {
    id: "number",
    type: "number",
    label: "Number Input",
    sublabel: "",
    placeholder: "Enter number ",
    icon: Hash,
  },
  {
    id: "phone",
    type: "phone",
    label: "Phone Number",
    sublabel: "",
    placeholder: "Enter phone number ",
    icon: Phone,
  },
  {
    id: "email",
    type: "email",
    label: "Email Input",
    sublabel: "",
    placeholder: "Enter email ",
    icon: Mail,
  },
  {
    id: "textarea",
    type: "textarea",
    label: "Text Area",
    sublabel: "",
    placeholder: "Enter long text ",
    max_length: "",
    icon: MessageSquare,
  },
  {
    id: "select",
    type: "select",
    label: "Dropdown",
    sublabel: "",
    placeholder: "Enter an option ",
    icon: ChevronDownSquare,
  },
  //Radio
  {
    id: "radio",
    type: "radio",
    label: "Radio Button",
    sublabel: "",
    placeholder: "Enter radio button ",
    icon: CircleDot,
  },
  //Checkbox
  {
    id: "checkbox",
    type: "checkbox",
    label: "Checkbox",
    sublabel: "",
    placeholder: "Enter checkbox ",
    icon: CheckSquare,
  },
  {
    id: "image",
    type: "file",
    label: "Upload File",
    sublabel: "",
    placeholder: "Enter upload or drag an file or image ",
    icon: FileUp,
  },
  {
    id: "spacer",
    type: "spacer",
    label: "Spacer Line",
    sublabel: "",
    height: 2,
    icon: Minus,
  },
  {
    id: "date",
    type: "date",
    label: "Date Picker",
    sublabel: "",
    placeholder: "Select date ",
    icon: Calendar,
  },
  {
    id: "university_repeatable",
    type: "university_repeatable",
    label: "University Repeatable",
    sublabel: "",
    placeholder: "Add university details",
    icon: Copy,
  },

  // {
  //   id: "wysiwyg",
  //   type: "wysiwyg",
  //   label: "Rich Text Editor",
  //   sublabel: "",
  //   placeholder: "Enter rich text content ",
  // },
];

const FormBuilderColumns = ({ form, onFieldsChange, smartRules = [] }) => {
  const [selectedField, setSelectedField] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [formFields, setFormFields] = useState([]);
  const drawerRef = useRef(null);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target)) {
        setIsDrawerOpen(false);
        setSelectedField(null);
      }
    };

    if (isDrawerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDrawerOpen]);

  // When a form is passed in, initialize the formFields state
  useEffect(() => {
    // console.log("Form received in FormBuilderColumns:", form);
    if (form) {
      // Convert the API fields to your internal structure
      // const convertedFields = (form.fields || [])
      //   .map((field) => ({
      //     ...field,
      //     id: field.id || uuidv4(),
      //     type: field.field_type || field.type,
      //     required: field.is_required === "1",
      //     options: field.options
      //       ? field.options.split(",").map((opt) => opt.trim())
      //       : field.options,
      //   }))
      //   .sort((a, b) => parseInt(a.field_order) - parseInt(b.field_order));

      const convertedFields = (form.fields || [])
        .map((field) => {
          const baseField = {
            // preserve existing properties if already in new structure
            ...field,
            id: field.id || uuidv4(),
            type: field.field_type || field.type,
            is_required: field.is_required == "1",
          };

          // Special handling for university_repeatable fields when loading for editing
          if ((field.field_type || field.type) === "university_repeatable") {
            try {
              if (field.options) {
                // Parse the JSON options to extract labels and dropdown options
                const structuredOptions = JSON.parse(field.options);
                console.log("Loading university field:", structuredOptions);

                const fieldLabels = {};
                let universityOptions = [];
                let degreeOptions = [];
                let fieldOfStudyOptions = [];

                // Extract data from structured options
                structuredOptions.forEach((subField, index) => {
                  console.log(`SubField ${index}:`, subField);

                  if (subField.field_type === "select") {
                    const labelLower = subField.label.toLowerCase();

                    // More flexible matching for university field (check for first occurrence or specific patterns)
                    if (
                      index === 0 ||
                      labelLower.includes("university") ||
                      labelLower.includes("name") ||
                      labelLower.includes("institution")
                    ) {
                      if (!fieldLabels.universityLabel) {
                        // Only set if not already set
                        fieldLabels.universityLabel = subField.label;
                        universityOptions = subField.options || [];
                        console.log(
                          "Found university field:",
                          subField.label,
                          universityOptions,
                        );
                      }
                    } else if (
                      labelLower.includes("degree") ||
                      labelLower.includes("qualification") ||
                      labelLower.includes("level")
                    ) {
                      fieldLabels.degreeLabel = subField.label;
                      degreeOptions = subField.options || [];
                      console.log(
                        "Found degree field:",
                        subField.label,
                        degreeOptions,
                      );
                    } else if (
                      labelLower.includes("study") ||
                      labelLower.includes("subject") ||
                      labelLower.includes("field") ||
                      labelLower.includes("major")
                    ) {
                      fieldLabels.fieldOfStudyLabel = subField.label;
                      fieldOfStudyOptions = subField.options || [];
                      console.log(
                        "Found field of study:",
                        subField.label,
                        fieldOfStudyOptions,
                      );
                    }
                  } else if (subField.field_type === "number") {
                    const labelLower = subField.label.toLowerCase();
                    if (
                      labelLower.includes("graduation") ||
                      labelLower.includes("year")
                    ) {
                      fieldLabels.graduationYearLabel = subField.label;
                      console.log("Found graduation year:", subField.label);
                    } else if (
                      labelLower.includes("gpa") ||
                      labelLower.includes("grade")
                    ) {
                      fieldLabels.gpaLabel = subField.label;
                      console.log("Found GPA field:", subField.label);
                    }
                  } else if (subField.field_type === "text") {
                    const labelLower = subField.label.toLowerCase();
                    if (
                      labelLower.includes("university") ||
                      labelLower.includes("name") ||
                      labelLower.includes("institution")
                    ) {
                      fieldLabels.universityLabel = subField.label;
                      console.log(
                        "Found university text field:",
                        subField.label,
                      );
                    } else if (
                      labelLower.includes("degree") ||
                      labelLower.includes("qualification") ||
                      labelLower.includes("level")
                    ) {
                      fieldLabels.degreeLabel = subField.label;
                      console.log("Found degree text field:", subField.label);
                    } else if (
                      labelLower.includes("study") ||
                      labelLower.includes("subject") ||
                      labelLower.includes("field") ||
                      labelLower.includes("major")
                    ) {
                      fieldLabels.fieldOfStudyLabel = subField.label;
                      console.log(
                        "Found field of study text field:",
                        subField.label,
                      );
                    }
                  }
                });

                // Set up the field with extracted data
                baseField.fieldLabels = fieldLabels;
                baseField.universityOptions = universityOptions;
                baseField.degreeOptions = degreeOptions;
                baseField.fieldOfStudyOptions = fieldOfStudyOptions;
                baseField.options = []; // Set to empty array for form builder

                console.log("Parsed university field for editing:", baseField);
              }
            } catch (error) {
              console.error("Error parsing university options:", error);
              baseField.options = [];
            }
          } else {
            // Handle regular fields (non-university)
            baseField.options = field.options
              ? field.options.split(",").map((opt) => opt.trim())
              : field.options;
          }

          return baseField;
        })
        .sort((a, b) => parseInt(a.field_order) - parseInt(b.field_order));

      setFormFields(convertedFields);
    } else {
      setFormFields([]);
    }
  }, [form]);

  // This effect propagates any changes in formFields up to the parent component
  useEffect(() => {
    if (onFieldsChange) {
      onFieldsChange(formFields);
    }
  }, [formFields, onFieldsChange]);

  const onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    // If dropping in the same place
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // If reordering within form preview
    if (
      source.droppableId === "form-preview" &&
      destination.droppableId === "form-preview"
    ) {
      const newFields = Array.from(formFields);
      const [movedField] = newFields.splice(source.index, 1);
      newFields.splice(destination.index, 0, movedField);
      setFormFields(newFields);
      return;
    }

    // If dragging from components to form preview
    if (
      source.droppableId === "components" &&
      destination.droppableId === "form-preview"
    ) {
      const componentType = draggableId;
      const component = formComponents.find((c) => c.type === componentType);

      if (component) {
        const newField = { ...component, id: uuidv4() };
        const newFields = Array.from(formFields);
        newFields.splice(destination.index, 0, newField);
        setFormFields(newFields);
      }
    }
  };

  const handleAddField = (field) => {
    setFormFields((prev) => [...prev, field]);
  };

  const handleDeleteField = (fieldId) => {
    setFormFields((prev) => prev.filter((field) => field.id !== fieldId));
    // Close drawer if deleting the selected field
    if (selectedField?.id === fieldId) {
      setIsDrawerOpen(false);
      setSelectedField(null);
    }
  };

  const handleMoveField = (fieldId, direction) => {
    const newFields = Array.from(formFields);
    const index = newFields.findIndex((field) => field.id === fieldId);
    if (index !== -1) {
      if (direction === "up") {
        if (index > 0) {
          [newFields[index - 1], newFields[index]] = [
            newFields[index],
            newFields[index - 1],
          ];
        }
      } else if (direction === "down") {
        if (index < newFields.length - 1) {
          [newFields[index], newFields[index + 1]] = [
            newFields[index + 1],
            newFields[index],
          ];
        }
      }
      setFormFields(newFields);
    }
  };

  const handleFieldSelect = (fieldId) => {
    const field = formFields.find((f) => f.id === fieldId);
    setSelectedField(field);
    setIsDrawerOpen(true);
  };

  const handleFieldUpdate = (updates) => {
    if (!selectedField) return;
    console.log("updates", updates);

    setFormFields((prev) =>
      prev.map((field) =>
        field.id === selectedField.id ? { ...field, ...updates } : field,
      ),
    );
    setSelectedField((prev) => ({ ...prev, ...updates }));
  };

  const handleDuplicateField = (fieldToDuplicate) => {
    // Create a deep copy of the field
    const duplicatedField = JSON.parse(JSON.stringify(fieldToDuplicate));

    // Generate a new unique ID for the duplicated field
    duplicatedField.id = `field-${Date.now()}`;

    // Find the index of the original field
    const originalIndex = formFields.findIndex(
      (field) => field.id === fieldToDuplicate.id,
    );

    // Insert the duplicated field right after the original
    const newFields = [
      ...formFields.slice(0, originalIndex + 1),
      duplicatedField,
      ...formFields.slice(originalIndex + 1),
    ];

    // Update the form state
    setFormFields(newFields);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="grid grid-cols-12 gap-6">
        {/* Left Column - Components Panel */}
        <div className="col-span-4">
          <div className="bg-muted p-6 rounded-lg shadow-sm border border-brand/20">
            <div className="flex items-center gap-2 mb-4">
              <Type className="w-5 h-5 text-foreground" />
              <h2 className="text-lg font-semibold text-foreground">Form Fields</h2>
            </div>
            <Droppable droppableId="components" isDropDisabled={true}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="space-y-3"
                >
                  {formComponents.map((component, index) => (
                    <DraggableComponent
                      key={`componenet-${component.type}`}
                      field={component}
                      index={index}
                      onAdd={handleAddField}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </div>

        {/* Middle Column - Form Preview */}
        <div className="col-span-8">
          <div className="sticky top-0 bg-card border-b  ">
            <div className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="flex items-center space-x-2">
                  <Palette className="w-5 h-5 text-foreground" />
                  <h2 className="text-lg font-semibold text-foreground">
                    Form Preview
                  </h2>
                </div>
                <p className="mt-1 pl-7 text-foreground font-medium text-sm">
                  The fields that CANNOT be changed after going live.
                </p>
              </div>
              {/* 
              <button
                // onClick={handlePreview}
                className="flex items-center space-x-1 px-3 py-1.5 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
              >
                <Eye className="w-4 h-4" />
                <span>Preview</span>
              </button> */}
            </div>
          </div>
          <div className="bg-muted p-6 rounded-lg shadow-sm border border-brand/20 min-h-[500px]">
            <Droppable droppableId="form-preview">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`space-y-3 min-h-[500px] rounded-lg transition-colors ${
                    snapshot.isDraggingOver ? "bg-primary/10" : ""
                  }`}
                >
                  {formFields.map((field, index) => (
                    <FormComponentWithDialog
                      key={field.id}
                      field={field}
                      index={index}
                      onDelete={handleDeleteField}
                      onMove={handleMoveField}
                      onSelect={handleFieldSelect}
                      onDuplicate={handleDuplicateField}
                      onUpdate={handleFieldUpdate}
                      smartRules={smartRules}
                    />
                  ))}
                  {provided.placeholder}
                  {formFields.length === 0 && !snapshot.isDraggingOver && (
                    // <div className="flex items-center justify-center h-96 border-2 border-dashed border-brand/30 rounded-lg">
                    //   <p className="text-foreground">Drag and drop components here to build your form</p>
                    // </div>
                    <div className="flex flex-col items-center justify-center h-96 border-2 border-dashed border-brand/30 rounded-lg bg-card/50">
                      <p className="text-foreground text-lg font-medium mb-2">
                        Drag and drop form fields from the left panel
                      </p>
                      <p className="text-foreground/80 text-sm">
                        Start building your form
                      </p>
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </div>
        </div>
      </div>

      {/* Field Options Drawer */}
      <div ref={drawerRef}>
        <FieldOptionsDrawer
          field={selectedField}
          onUpdate={handleFieldUpdate}
          onClose={() => {
            setIsDrawerOpen(false);
            setSelectedField(null);
          }}
          isOpen={isDrawerOpen}
        />
      </div>
    </DragDropContext>
  );
};

export default FormBuilderColumns;
